import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X } from 'lucide-react';

const GooglePlacesAutocomplete = ({
    value,
    onChange,
    placeholder,
    className,
    label,
    required = false,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);

    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);

    // Initialize Google Places services
    useEffect(() => {
        if (window.google && window.google.maps) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            // Create a dummy div for PlacesService
            const dummyDiv = document.createElement('div');
            placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
    }, []);

    // Update input value when prop changes
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const searchPlaces = async (query) => {
        if (!query.trim() || !autocompleteService.current) {
            setSuggestions([]);
            return;
        }

        setIsLoading(true);

        try {
            const request = {
                input: query,
                componentRestrictions: { country: 'in' },
                types: ['establishment', 'geocode'],
            };

            autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
                setIsLoading(false);

                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    const formattedSuggestions = predictions.map(prediction => ({
                        placeId: prediction.place_id,
                        description: prediction.description,
                        structuredFormatting: prediction.structured_formatting,
                        types: prediction.types
                    }));
                    setSuggestions(formattedSuggestions);
                    setShowSuggestions(true);
                } else {
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            });
        } catch (error) {
            console.error('Error searching places:', error);
            setIsLoading(false);
            setSuggestions([]);
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setSelectedPlace(null);

        // Notify parent of the change
        if (onChange) {
            onChange(e, null);
        }

        // Clear existing debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Debounce search
        debounceRef.current = setTimeout(() => {
            searchPlaces(newValue);
        }, 300);
    };

    const getPlaceDetails = (placeId) => {
        return new Promise((resolve, reject) => {
            if (!placesService.current) {
                reject(new Error('Places service not initialized'));
                return;
            }

            const request = {
                placeId: placeId,
                fields: [
                    'place_id',
                    'formatted_address',
                    'name',
                    'geometry',
                    'address_components',
                    'types'
                ]
            };

            placesService.current.getDetails(request, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    resolve(place);
                } else {
                    reject(new Error(`Places service failed: ${status}`));
                }
            });
        });
    };

    const handleSuggestionClick = async (suggestion) => {
        try {
            setIsLoading(true);
            const placeDetails = await getPlaceDetails(suggestion.placeId);

            const placeData = {
                placeId: suggestion.placeId,
                formatted_address: placeDetails.formatted_address,
                name: placeDetails.name,
                lat: placeDetails.geometry.location.lat(),
                lng: placeDetails.geometry.location.lng(),
                types: placeDetails.types,
                address_components: placeDetails.address_components
            };

            setSelectedPlace(placeData);
            setInputValue(placeDetails.formatted_address);
            setShowSuggestions(false);

            // Call onChange with detailed place information
            onChange && onChange({
                target: {
                    value: placeDetails.formatted_address
                }
            }, placeData);

        } catch (error) {
            console.error('Error getting place details:', error);
            // Fallback to just the description
            setInputValue(suggestion.description);
            setShowSuggestions(false);
            onChange && onChange({
                target: {
                    value: suggestion.description
                }
            }, { formatted_address: suggestion.description });
        } finally {
            setIsLoading(false);
        }
    };

    const clearSelection = () => {
        setInputValue('');
        setSelectedPlace(null);
        setSuggestions([]);
        setShowSuggestions(false);
        onChange && onChange({ target: { value: '' } }, null);
        inputRef.current?.focus();
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = () => {
        // Delay hiding suggestions to allow click events
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };

    return (
        <div className="space-y-1 relative">
            {label && (
                <label className="block text-sm font-medium text-gray-700">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div className="relative">
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={placeholder}
                        className={`w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className || ''}`}
                        {...props}
                    />

                    <div className="absolute inset-y-0 right-0 flex items-center">
                        {inputValue && (
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="p-1 text-gray-400 hover:text-gray-600 mr-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                    </div>
                </div>

                {/* Loading indicator */}
                {isLoading && (
                    <div className="absolute right-8 top-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                )}

                {/* Suggestions dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {suggestions.map((suggestion, index) => (
                            <div
                                key={suggestion.placeId}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                                <div className="flex items-start space-x-3">
                                    <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                            {suggestion.structuredFormatting?.main_text || suggestion.description}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                            {suggestion.structuredFormatting?.secondary_text || ''}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Selected place info */}
            {selectedPlace && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center text-sm text-green-800">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="font-medium">Selected: </span>
                        <span className="ml-1 truncate">{selectedPlace.name || selectedPlace.formatted_address}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GooglePlacesAutocomplete;